import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { AtiraEmpty } from '../../../components/AtiraEmpty';
import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { SpinnerFullScreen } from '../../../components/SpinnerFullScreen';
import { Text } from '../../../components/Text';
import { adminSliceSelectors } from '../../../redux/admin/admin.selector';
import { adminActions } from '../../../redux/admin/admin.slice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { userSliceSelectors } from '../../../redux/user/user.selector';
import { FontSize } from '../../../theme/FontSize';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { AdminMessage } from './AdminMessage';

const Container = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding-inline: ${Spacing.s};
`;

const StyledButton = styled(Button)`
  font-size: ${FontSize.s};
  background-color: transparent;
  border: ${({ theme }) => `1px solid ${theme.main}`};
  color: ${({ theme }) => theme.main};
  height: 2.3rem;

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.lightTextColor};
  }
`;

export const NotificationsAdminMessagesTab: React.FC = () => {
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);

  const { t } = useTranslation(undefined, {
    keyPrefix: 'bell.drawer.adminMessages',
  });
  const dispatch = useAppDispatch();

  const adminMessages = useAppSelector(adminSliceSelectors.selectAdminMessages);
  const adminMessagesLoading = useAppSelector(
    adminSliceSelectors.selectAdminMessagesLoading,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const adminMessagesPage = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPage,
  );
  const adminMessagesPageSize = useAppSelector(
    adminSliceSelectors.selectAdminMessagesPageSize,
  );

  const onMarkAsRead = async () => {
    try {
      setMarkAsReadLoading(true);

      await dispatch(adminActions.markAdminMessagesAsRead({ userId })).unwrap();
      await dispatch(
        adminActions.getAdminMessages({
          userId,
          meta: { page: adminMessagesPage - 1, count: adminMessagesPageSize },
        }),
      ).unwrap();

      AtiraToast.success(t('mark_as_read.success'));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  if (adminMessagesLoading && !adminMessages?.length) {
    return <SpinnerFullScreen />;
  }

  return (
    <Container>
      {adminMessages?.length ? (
        <Flex flexDirection="column" gap="s">
          <Text fontSize="s">{t('title')}</Text>

          <StyledButton onClick={onMarkAsRead} loading={markAsReadLoading}>
            {t('mark_as_read')}
          </StyledButton>
        </Flex>
      ) : null}

      {adminMessages?.length ? (
        <Flex flexDirection="column" gap="s">
          {adminMessages.map((message) => (
            <AdminMessage key={message._id} message={message} />
          ))}
        </Flex>
      ) : (
        <AtiraEmpty description={t('empty')} />
      )}
    </Container>
  );
};
